import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Separator } from '@/shared/components/ui/separator';
import { Badge } from '@/shared/components/ui/badge';
import { useToast } from '@/shared/components/ui/use-toast';
import { useStarredItemMutations } from '@/features/dashboard/hooks/useDashboard';
import type { DashboardPreferences } from '@/shared/types/api';
import { LayoutDashboard, FlaskConical } from 'lucide-react';

interface UserSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboardPreferences: DashboardPreferences;
  onSaved: () => void;
}

const DAYS_OPTIONS = [1, 3, 7, 14, 30];

export default function UserSettingsDialog({
  open,
  onOpenChange,
  dashboardPreferences,
  onSaved,
}: Readonly<UserSettingsDialogProps>) {
  const [dashboardEnabled, setDashboardEnabled] = useState(dashboardPreferences.enabled);
  const [showRecentChanges, setShowRecentChanges] = useState(dashboardPreferences.show_recent_changes);
  const [showStatistics, setShowStatistics] = useState(dashboardPreferences.show_statistics);
  const [recentChangesDays, setRecentChangesDays] = useState(dashboardPreferences.recent_changes_days);
  const [enableAnalytics, setEnableAnalytics] = useState(dashboardPreferences.enable_analytics);
  const { updateDashboardPreferences, saving } = useStarredItemMutations();
  const { toast } = useToast();

  // Sync local state when preferences prop changes
  useEffect(() => {
    setDashboardEnabled(dashboardPreferences.enabled);
    setShowRecentChanges(dashboardPreferences.show_recent_changes);
    setShowStatistics(dashboardPreferences.show_statistics);
    setRecentChangesDays(dashboardPreferences.recent_changes_days);
    setEnableAnalytics(dashboardPreferences.enable_analytics);
  }, [dashboardPreferences]);

  const handleSave = async () => {
    try {
      await updateDashboardPreferences({
        enabled: dashboardEnabled,
        show_recent_changes: showRecentChanges,
        show_statistics: showStatistics,
        recent_changes_days: recentChangesDays,
        enable_analytics: enableAnalytics,
      });
      toast({ title: 'Settings saved' });
      globalThis.dispatchEvent(new Event('dashboard:refresh'));
      onSaved();
      onOpenChange(false);
    } catch {
      toast({ title: 'Failed to save settings', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>
            Manage your personal preferences
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Dashboard Enabled */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="h-5 w-5 text-muted-foreground" />
              <div className="space-y-0.5">
                <Label htmlFor="dashboard-enabled">Enable Dashboard</Label>
                <p className="text-xs text-muted-foreground">
                  Show the dashboard as your landing page with starred projects and activity.
                </p>
              </div>
            </div>
            <Switch
              id="dashboard-enabled"
              checked={dashboardEnabled}
              onCheckedChange={setDashboardEnabled}
            />
          </div>

          {/* Dashboard settings — shown only when dashboard is enabled */}
          {dashboardEnabled && (
            <>
              <Separator />
              <div className="space-y-5 pl-8">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Dashboard Settings
                </p>

                {/* Show Statistics */}
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="show-statistics">Show Statistics</Label>
                    <p className="text-xs text-muted-foreground">
                      Display the statistics strip at the top of the dashboard.
                    </p>
                  </div>
                  <Switch
                    id="show-statistics"
                    checked={showStatistics}
                    onCheckedChange={setShowStatistics}
                  />
                </div>

                {/* Show Recent Changes */}
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="show-recent-changes">Show Recent Changes</Label>
                    <p className="text-xs text-muted-foreground">
                      Display the recent activity feed on the dashboard.
                    </p>
                  </div>
                  <Switch
                    id="show-recent-changes"
                    checked={showRecentChanges}
                    onCheckedChange={setShowRecentChanges}
                  />
                </div>

                {/* Recent Changes Days */}
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="recent-changes-days">Recent Changes Period</Label>
                    <p className="text-xs text-muted-foreground">
                      Number of days to include in recent changes.
                    </p>
                  </div>
                  <Select
                    value={String(recentChangesDays)}
                    onValueChange={(v) => setRecentChangesDays(Number(v))}
                    disabled={!showRecentChanges}
                  >
                    <SelectTrigger id="recent-changes-days" className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OPTIONS.map((d) => (
                        <SelectItem key={d} value={String(d)}>
                          {d} {d === 1 ? 'day' : 'days'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {/* Experimental Features */}
          <Separator />
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FlaskConical className="h-5 w-5 text-muted-foreground" />
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="enable-analytics">Analytics</Label>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium text-amber-600 border-amber-400">
                    Experimental
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Show the analytics tab on projects to visualize container version flow across environments.
                </p>
              </div>
            </div>
            <Switch
              id="enable-analytics"
              checked={enableAnalytics}
              onCheckedChange={setEnableAnalytics}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
